import { useSetAtom } from 'jotai';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { UseFormReturn, UseFormSetValue } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import {
  useCatalogDataContextRefreshEventForItemsSelector,
  useCatalogDataContextUpdateItemInEventSelector,
} from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useEventHubContextOnUpdateListingSelector } from 'src/contexts/EventHubContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useSearchDataContextUpdateItemInEventSelector } from 'src/contexts/SearchDataContext';
import {
  Alert,
  AlertWithSuppressionDialog,
} from 'src/dialogs/AlertWithSuppressionDialog';
import { useAutoBroadcastSellerAccountSettings } from 'src/hooks/api/useAutoBroadcastSellerAccountSettings';
import { useConfirmationDialogSettings } from 'src/hooks/api/useConfirmationDialogSettings';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryAutoBroadcastListing } from 'src/utils/autoBroadcastUtils';
import {
  validateAutoPricingSettings,
  validateCompListingSettings,
  validateUndercutSettings,
} from 'src/utils/autoPricingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { isDatePassedHours } from 'src/utils/dateTimeUtils';
import { isSuccess } from 'src/utils/errorUtils';
import {
  getListingDetailsUpdateInput,
  isAbsolutePriceDrop,
  isRelativeFloorPriceDrop,
  isRelativePriceDrop,
  isSuspiciousPriceChange,
} from 'src/utils/inventoryUtils';
import { posFieldFromValues } from 'src/utils/posFieldUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  Feature,
  Listing,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdates,
  ListingDetailsPricingSectionUpdates,
  ListingDetailsUpdateInput,
  ListingPricingSectionPosFieldUpdates,
  PosUiActionResult,
} from 'src/WebApiController';
import { createContext } from 'use-context-selector';

import { ListingPricingInput } from '../configs/ListingPriceForm.types';
import { updateListingLoadingAtom } from './ListingTableActionContext.atoms';
import { useSellerEventDialogSuppressions } from './ListingTableActionContext.hooks';
import {
  ListingTableActionContextType,
  SubmitListingPriceParams,
  SubmitPricingSettingsParams,
  WarningDialogRequest,
} from './ListingTableActionContext.types';

export const ListingTableActionContext =
  createContext<ListingTableActionContextType>({
    onSubmitListingPriceChange: () => {},
    onSubmitPricingSettingsChange: () => {},
  });

const getUpdateValue = <T,>(
  value: T | null,
  defaultValue?: T | null,
  fallback?: T | null
): T | null => {
  if (value === defaultValue) {
    return null;
  }
  return value ?? fallback ?? null;
};

export const ListingTableActionContextProvider = ({
  viagogoVirtualIds,
  children,
}: {
  viagogoVirtualIds: string[];
  children: React.ReactNode;
}) => {
  // --- Shared dialog ---
  const warningDialog = useBasicDialog();
  const [dialogRequest, setDialogRequest] =
    useState<WarningDialogRequest | null>(null);
  // --- Feature flags ---
  const hasFixUpdateFloorAndCeilingIssuesFeature = useUserHasFeature(
    Feature.FixUpdateFloorAndCeilingIssues
  );
  const { data: autoBroadcastSettings } =
    useAutoBroadcastSellerAccountSettings();
  const hasAutoBroadcastFeature = useUserHasFeature(
    Feature.AutoBroadcastWhenPricing
  );
  const hasAllowSeatScoreCompListingMode = useUserHasFeature(
    Feature.AllowSeatScoreCompListingMode
  );
  const hasRemovePriceIncreaseRoadbump = useUserHasFeature(
    Feature.RemovePriceIncreaseRoadbump
  );
  const hasSellerEventAlertMutes = useUserHasFeature(
    Feature.SellerEventAlertMutes
  );

  const onUpdateListingLoading = useSetAtom(updateListingLoadingAtom);
  const { getDialogProps } = useSellerEventDialogSuppressions(
    viagogoVirtualIds,
    hasSellerEventAlertMutes
  );

  // --- App-level dependencies (instantiated once, not per row) ---
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const updateItemInEvent = useCatalogDataContextUpdateItemInEventSelector();
  const refreshEventForItems =
    useCatalogDataContextRefreshEventForItemsSelector();
  const updateSearchDataItem = useSearchDataContextUpdateItemInEventSelector();
  const onUpdateListing = useEventHubContextOnUpdateListingSelector();

  const { getUiCurrency } = useLocalizationContext();

  // --- Confirmation dialog settings ---
  const { confirmationDialogSettings } = useConfirmationDialogSettings();

  // --- Content strings for validation ---
  const requiredMsg = useContent(ContentId.Required);
  const floorMustBeLessThanCeilingError = useContent(
    ContentId.FloorMustBeLessThanCeiling
  );
  const compListingFloorCeilingError = useContent(
    ContentId.CompListingFloorMustLessThanCeiling
  );

  // --- Show warning dialog ---
  const showWarning = useCallback(
    (req: WarningDialogRequest) => {
      setDialogRequest(req);
      warningDialog.launchDialog();
    },
    [warningDialog]
  );

  // --- API: submit listing price ---
  const executeSubmitPriceChange = useCallback(
    (listing: Listing, formMethods: UseFormReturn<ListingPricingInput>) => {
      if (!activeAccountWebClientConfig.activeAccountId) return;
      onUpdateListingLoading({ id: listing.id, isLoading: true });

      const values = formMethods.getValues();
      const defaultValues = formMethods.formState.defaultValues;

      tryInvokeApi(
        async () => {
          const client = new ListingClient(activeAccountWebClientConfig);
          let result: PosUiActionResult | null = null;
          if (hasFixUpdateFloorAndCeilingIssuesFeature) {
            const updateInput: ListingPricingSectionPosFieldUpdates = {
              unitNetProceeds: posFieldFromValues(
                values.listPrice,
                defaultValues?.listPrice
              ),
              autoPricingEnabled: posFieldFromValues(
                values.autoPricingEnabled,
                defaultValues?.autoPricingEnabled
              ),
              netProceedsFloor: posFieldFromValues(
                values.netProceedsFloor,
                defaultValues?.netProceedsFloor
              ),
              netProceedsCeiling: posFieldFromValues(
                values.netProceedsCeiling,
                defaultValues?.netProceedsCeiling
              ),
              pricerSellerUserId: posFieldFromValues(
                listing.pricerId,
                defaultValues?.pricerSellerUserId
              ),
            };
            result = await client.updateListingPricingSectionFromListingTable(
              listing.id,
              updateInput
            );
          } else {
            const input: ListingDetailsPricingSectionUpdates = {
              id: listing.id,
              rowVersion: null,
              listPrice: getUpdateValue(
                values.listPrice,
                defaultValues?.listPrice,
                values.listPrice
              ),
              allInPrice: null,
              buyerUserId: null,
              marketplacePriceUpdates: [],
              netProceedsFloor: getUpdateValue(
                values.netProceedsFloor,
                defaultValues?.netProceedsFloor,
                values.netProceedsFloor
              ),
              netProceedsCeiling: getUpdateValue(
                values.netProceedsCeiling,
                defaultValues?.netProceedsCeiling,
                values.netProceedsCeiling
              ),
              autoPricingEnabled: getUpdateValue(
                values.autoPricingEnabled,
                defaultValues?.autoPricingEnabled,
                values.autoPricingEnabled
              ),
              pricerSellerUserId: listing.pricerId,
              currencyCode: listing.currency,
            };
            result = await client.updateListingPricingSectionByListingId(
              input.id,
              input
            );
          }

          if (isSuccess(result)) {
            try {
              await tryAutoBroadcastListing({
                client,
                listingId: listing.id,
                listingStatus: listing.status,
                effectivePrice: values.listPrice ?? values.allInPrice,
                autoBroadcastEnabled:
                  autoBroadcastSettings?.autoBroadcastManuallyPricedListings,
                hasFeature: hasAutoBroadcastFeature,
              });
            } catch {
              // Broadcast failures should not break the price update flow
            }
            const updated = await client.getListingByListingId(
              listing.id,
              listing as ListingDetails,
              [ListingDetailDataField.Pricing]
            );
            if (updated) {
              updateItemInEvent(updated, ActionOutboxEntityType.Listing);
              updateSearchDataItem(updated, ActionOutboxEntityType.Listing);
              onUpdateListing(updated.id);
            }
            if (updated.ltGrpId != null) {
              await refreshEventForItems(
                [updated.id],
                ActionOutboxEntityType.Listing
              );
            }
          }
        },
        () => {},
        () => {
          onUpdateListingLoading({ id: listing.id, isLoading: false });
        }
      );
    },
    [
      activeAccountWebClientConfig,
      autoBroadcastSettings,
      hasAutoBroadcastFeature,
      hasFixUpdateFloorAndCeilingIssuesFeature,
      updateItemInEvent,
      updateSearchDataItem,
      onUpdateListing,
      refreshEventForItems,
      onUpdateListingLoading,
    ]
  );

  // --- Public: handle isSubmitting flow (alert checking + price change) ---
  const onSubmitListingPriceChange = useCallback(
    ({ listing, formMethods }: SubmitListingPriceParams) => {
      const { setValue, clearErrors, setError, getValues, formState } =
        formMethods;
      const values = getValues();
      const defaultValues = formState.defaultValues;
      const listPrice = values.listPrice ?? -1;
      const allInPrice = values.allInPrice ?? -1;
      const netProceedsFloor = values.netProceedsFloor;
      const netProceedsCeiling = values.netProceedsCeiling;
      const autoPricingEnabled = values.autoPricingEnabled;

      // Only allow updating list/allin prices, or netProceedsFloor
      if (listPrice < 0 && allInPrice < 0 && !netProceedsFloor) {
        return;
      }

      if (
        listPrice === defaultValues?.listPrice &&
        allInPrice === defaultValues?.allInPrice &&
        netProceedsFloor === defaultValues?.netProceedsFloor
      ) {
        return;
      }

      clearErrors('listPrice');
      clearErrors('netProceedsFloor');

      let hasErrors = false;
      if (listPrice < 0) {
        setError('listPrice', { message: requiredMsg });
        hasErrors = true;
      }
      if (netProceedsFloor && netProceedsFloor < 0) {
        setError('netProceedsFloor', { message: requiredMsg });
        hasErrors = true;
      }
      if (hasErrors) {
        return;
      }

      const alignFloorWithProceeds = () => {
        if (listPrice < (netProceedsFloor ?? Number.MIN_VALUE)) {
          setValue('netProceedsFloor', listPrice);
        } else if (listPrice > (netProceedsCeiling ?? Number.MAX_VALUE)) {
          setValue('netProceedsCeiling', listPrice);
        }
      };

      const revertFormValues = () => {
        setValue('listPrice', defaultValues?.listPrice ?? null);
        setValue('allInPrice', defaultValues?.allInPrice ?? null);
        setValue('netProceedsFloor', defaultValues?.netProceedsFloor ?? null);
        setValue(
          'netProceedsCeiling',
          defaultValues?.netProceedsCeiling ?? null
        );
      };

      const doSubmit = () => executeSubmitPriceChange(listing, formMethods);

      const newAlerts: Alert[] = [];

      const {
        manualPriceAdjustFloorCeilingProps,
        manualPriceDisableAutoPricingProps,
        suspiciousPriceChangeHookProps,
        absolutePriceDropProps,
        relativeFloorPriceDropProps,
        relativePriceDropProps,
      } = getDialogProps(listing.viagVirtId);

      if (listPrice > 0 && listPrice !== defaultValues?.listPrice) {
        // FloorCeiling Soft Block
        const isFloorCeilingDialogEnabled =
          confirmationDialogSettings?.settings?.FloorCeilingViolation
            ?.isActive ?? true;

        if (
          listPrice < (netProceedsFloor ?? Number.MIN_VALUE) ||
          listPrice > (netProceedsCeiling ?? Number.MAX_VALUE)
        ) {
          if (
            isDatePassedHours(
              manualPriceAdjustFloorCeilingProps.lastTimeStamp,
              1
            ) &&
            isFloorCeilingDialogEnabled
          ) {
            newAlerts.push({
              ...manualPriceAdjustFloorCeilingProps,
              onOkay: () => alignFloorWithProceeds(),
              onCancel: () => {
                if ((listPrice ?? 0) < (netProceedsFloor ?? Number.MIN_VALUE)) {
                  setValue('listPrice', netProceedsFloor);
                } else if (
                  (listPrice ?? 0) > (netProceedsCeiling ?? Number.MAX_VALUE)
                ) {
                  setValue('listPrice', netProceedsCeiling);
                }
              },
            });
          } else {
            alignFloorWithProceeds();
          }
        }

        // AutoPricing Disable when manual pricing soft block
        const isAutoPricingDisableDialogEnabled =
          confirmationDialogSettings?.settings?.AutoPricingDisable?.isActive ??
          true;

        if (
          isAutoPricingDisableDialogEnabled &&
          autoPricingEnabled &&
          isDatePassedHours(manualPriceDisableAutoPricingProps.lastTimeStamp, 1)
        ) {
          newAlerts.push({
            ...manualPriceDisableAutoPricingProps,
            onOkay: () => setValue('autoPricingEnabled', false),
          });
        }

        // Suspicious price change soft block
        const isSuspiciousPriceChangeDialogEnabled =
          confirmationDialogSettings?.settings?.SuspiciousPriceChange
            ?.isActive ?? true;

        if (
          isSuspiciousPriceChangeDialogEnabled &&
          isSuspiciousPriceChange(listPrice, defaultValues?.listPrice) &&
          !hasRemovePriceIncreaseRoadbump &&
          isDatePassedHours(suspiciousPriceChangeHookProps.lastTimeStamp, 1)
        ) {
          newAlerts.push({
            ...suspiciousPriceChangeHookProps,
            onCancel: revertFormValues,
          });
        }

        // Price drop dialogs
        if (hasSellerEventAlertMutes && hasRemovePriceIncreaseRoadbump) {
          const isAbsolutePriceDropEnabled =
            confirmationDialogSettings?.settings?.AbsolutePriceDrop?.isActive ??
            true;
          const absoluteThreshold =
            confirmationDialogSettings?.settings?.AbsolutePriceDrop
              ?.absoluteThreshold ?? Number.MAX_VALUE;

          if (
            isAbsolutePriceDropEnabled &&
            isAbsolutePriceDrop(
              listPrice,
              defaultValues?.listPrice,
              absoluteThreshold
            ) &&
            isDatePassedHours(absolutePriceDropProps.lastTimeStamp, 1)
          ) {
            newAlerts.push({
              ...absolutePriceDropProps,
              onCancel: revertFormValues,
            });
          }

          const isRelativePriceDropEnabled =
            confirmationDialogSettings?.settings?.RelativePriceDrop?.isActive ??
            true;
          const relativeThreshold =
            confirmationDialogSettings?.settings?.RelativePriceDrop
              ?.relativeThreshold ?? 25;

          if (
            isRelativePriceDropEnabled &&
            isRelativePriceDrop(
              listPrice,
              defaultValues?.listPrice,
              relativeThreshold
            ) &&
            isDatePassedHours(relativePriceDropProps.lastTimeStamp, 1)
          ) {
            newAlerts.push({
              ...relativePriceDropProps,
              onCancel: revertFormValues,
            });
          }

          const isRelativeFloorPriceDropEnabled =
            confirmationDialogSettings?.settings?.RelativeFloorPriceDrop
              ?.isActive ?? true;
          if (
            isRelativeFloorPriceDropEnabled &&
            isRelativeFloorPriceDrop(listPrice, listing.unitCst?.amt ?? null) &&
            isDatePassedHours(relativeFloorPriceDropProps.lastTimeStamp, 1)
          ) {
            newAlerts.push({
              ...relativeFloorPriceDropProps,
              onCancel: revertFormValues,
            });
          }
        }
      }

      if (newAlerts.length) {
        showWarning({
          alerts: newAlerts,
          viagogoVirtualId: listing.viagVirtId,
          onOkay: doSubmit,
          onCancel: revertFormValues,
        });
      } else {
        doSubmit();
      }
    },
    [
      getDialogProps,
      requiredMsg,
      executeSubmitPriceChange,
      confirmationDialogSettings?.settings?.FloorCeilingViolation?.isActive,
      confirmationDialogSettings?.settings?.AutoPricingDisable?.isActive,
      confirmationDialogSettings?.settings?.SuspiciousPriceChange?.isActive,
      confirmationDialogSettings?.settings?.AbsolutePriceDrop?.isActive,
      confirmationDialogSettings?.settings?.AbsolutePriceDrop
        ?.absoluteThreshold,
      confirmationDialogSettings?.settings?.RelativePriceDrop?.isActive,
      confirmationDialogSettings?.settings?.RelativePriceDrop
        ?.relativeThreshold,
      confirmationDialogSettings?.settings?.RelativeFloorPriceDrop?.isActive,
      hasRemovePriceIncreaseRoadbump,
      hasSellerEventAlertMutes,
      showWarning,
    ]
  );

  // --- Public: handle isSubmittingPricingSettings flow ---
  const onSubmitPricingSettingsChange = useCallback(
    ({ listing, formMethods, fieldName }: SubmitPricingSettingsParams) => {
      const { setValue, setError, clearErrors, getValues, formState, reset } =
        formMethods;
      const setPriceSettingValues =
        setValue as unknown as UseFormSetValue<ListingDetailsUpdateInput>;
      const values = getValues();
      const defaultValues = formState.defaultValues;

      // Check if any pricing setting actually changed
      if (
        values.autoPricingEnabled === defaultValues?.autoPricingEnabled &&
        values.undercutAbsoluteAmount ===
          defaultValues?.undercutAbsoluteAmount &&
        values.undercutRelativeAmount ===
          defaultValues?.undercutRelativeAmount &&
        values.undercutMode === defaultValues?.undercutMode &&
        values.compListingMode === defaultValues?.compListingMode &&
        values.compListingCeiling === defaultValues?.compListingCeiling &&
        values.compListingFloor === defaultValues?.compListingFloor &&
        values.compListingQuantityScoreAdjustmentEnabled ===
          defaultValues?.compListingQuantityScoreAdjustmentEnabled &&
        values.compListingOnlyForSelectedSectionsEnabled ===
          defaultValues?.compListingOnlyForSelectedSectionsEnabled &&
        values.compListingQuantityScoreAdjustmentOverrideJson ===
          defaultValues?.compListingQuantityScoreAdjustmentOverrideJson &&
        values.compListingOnlyForSameZoneEnabled ===
          defaultValues?.compListingOnlyForSameZoneEnabled &&
        values.compListingExcludeFanInventory ===
          defaultValues?.compListingExcludeFanInventory &&
        values.outlierMode === defaultValues?.outlierMode &&
        values.outlierStandardDeviations ===
          defaultValues?.outlierStandardDeviations &&
        values.outlierKthLowestLimit === defaultValues?.outlierKthLowestLimit &&
        values.outlierKthLowestLimitAbsoluteSpacing ===
          defaultValues?.outlierKthLowestLimitAbsoluteSpacing &&
        values.outlierKthLowestLimitRelativeSpacing ===
          defaultValues?.outlierKthLowestLimitRelativeSpacing &&
        values.circuitBreakerMinCompListingCount ===
          defaultValues?.circuitBreakerMinCompListingCount &&
        values.circuitBreakerMaxDiscountVelocityPercent ===
          defaultValues?.circuitBreakerMaxDiscountVelocityPercent &&
        values.circuitBreakerMaxDiscountVelocityTicksInHours ===
          defaultValues?.circuitBreakerMaxDiscountVelocityTicksInHours
      ) {
        return;
      }

      let hasErrors = false;

      if (values.autoPricingEnabled !== defaultValues?.autoPricingEnabled) {
        if (
          !validateAutoPricingSettings(
            clearErrors,
            setError,
            setPriceSettingValues,
            values,
            floorMustBeLessThanCeilingError,
            compListingFloorCeilingError,
            requiredMsg
          )
        ) {
          setValue('autoPricingEnabled', listing.isAutoPrc ?? false);
        }
      } else {
        if (
          values.compListingMode !== defaultValues?.compListingMode ||
          values.compListingCeiling !== defaultValues?.compListingCeiling ||
          values.compListingFloor !== defaultValues?.compListingFloor
        ) {
          const fieldEdited =
            values.compListingCeiling !== defaultValues?.compListingCeiling
              ? 'compListingCeiling'
              : values.compListingFloor !== defaultValues?.compListingFloor
                ? 'compListingFloor'
                : 'compListingMode';
          if (
            !validateCompListingSettings(
              clearErrors,
              setError,
              setPriceSettingValues,
              values,
              compListingFloorCeilingError,
              requiredMsg,
              fieldEdited
            )
          ) {
            hasErrors = true;
          }
        }

        if (
          values.undercutAbsoluteAmount !==
            defaultValues?.undercutAbsoluteAmount ||
          values.undercutRelativeAmount !==
            defaultValues?.undercutRelativeAmount ||
          values.undercutMode !== defaultValues?.undercutMode
        ) {
          const fieldEdited =
            values.undercutAbsoluteAmount !==
            defaultValues?.undercutAbsoluteAmount
              ? 'undercutAbsoluteAmount'
              : values.undercutRelativeAmount !==
                  defaultValues?.undercutRelativeAmount
                ? 'undercutRelativeAmount'
                : 'undercutMode';
          if (
            !validateUndercutSettings(
              clearErrors,
              setPriceSettingValues,
              values,
              fieldEdited
            )
          ) {
            hasErrors = true;
          }
        }
      }

      if (hasErrors) {
        return;
      }

      onUpdateListingLoading({ id: listing.id, isLoading: true });
      if (fieldName) {
        setValue('submittingField', fieldName);
      }
      if (values.rowIndex != null) {
        setValue('isSubmittingRowIndex', values.rowIndex);
      }
      // Execute API call
      tryInvokeApi(
        async () => {
          const client = new ListingClient(activeAccountWebClientConfig);
          const input: ListingDetailsAutoPricingSectionUpdates = {
            ...values,
            id: listing.id,
            rowVersion: null,
            autoPriceFrequency: null,
            compListingQuantityFilters: null,
            compListingQuantityScoreAdjustmentOverrides: null,
            scoreModel: null,
            autoPricingDefaultQuantityFiltersJson: null,
            venueZoneOverrideId: null,
            venueZoneConfigType: null,
            sectionRowScoreRecommendations: null,
            previewMarketPrices: null,
          };

          await client.updateListingAutoPricingSectionByListingId(
            input.id,
            input
          );

          // Auto-broadcast when autopricing is toggled ON
          if (
            values.autoPricingEnabled === true &&
            listing.isAutoPrc !== true
          ) {
            try {
              await tryAutoBroadcastListing({
                client,
                listingId: listing.id,
                listingStatus: listing.status,
                effectivePrice: listing.listPrice,
                autoBroadcastEnabled:
                  autoBroadcastSettings?.autoBroadcastManuallyPricedListings,
                hasFeature: hasAutoBroadcastFeature,
              });
            } catch {
              // Broadcast failures should not break the pricing settings update flow
            }
          }

          const updatedListing = await client.getListingByListingId(
            listing.id,
            listing as ListingDetails,
            [ListingDetailDataField.Pricing, ListingDetailDataField.AutoPricing]
          );
          if (updatedListing) {
            updateItemInEvent(updatedListing, ActionOutboxEntityType.Listing);
            updateSearchDataItem(
              updatedListing,
              ActionOutboxEntityType.Listing
            );
            const uiCurrency = getUiCurrency(updatedListing.currency);
            reset({
              ...getListingDetailsUpdateInput(
                updatedListing as ListingDetails,
                uiCurrency,
                undefined,
                null,
                null,
                false,
                null,
                hasAllowSeatScoreCompListingMode
              ),
              isSubmitting: false,
              rowIndex: values.rowIndex || 0,
              currentPageIndex: values.currentPageIndex || 0,
            });
            onUpdateListing(updatedListing.id);
          }
        },
        (error) => {
          showErrorDialog(
            'ListingClient.updateListing(pricingSettings)',
            error,
            { trackErrorData: values }
          );
        },
        () => {
          onUpdateListingLoading({ id: listing.id, isLoading: false });
          setValue('submittingField', undefined);
          setValue('isSubmittingRowIndex', undefined);
        }
      );
    },
    [
      floorMustBeLessThanCeilingError,
      compListingFloorCeilingError,
      requiredMsg,
      onUpdateListingLoading,
      activeAccountWebClientConfig,
      autoBroadcastSettings,
      hasAutoBroadcastFeature,
      updateItemInEvent,
      updateSearchDataItem,
      onUpdateListing,
      showErrorDialog,
      getUiCurrency,
      hasAllowSeatScoreCompListingMode,
    ]
  );

  const contextValue = useMemo(
    () => ({
      onSubmitListingPriceChange,
      onSubmitPricingSettingsChange,
    }),
    [onSubmitListingPriceChange, onSubmitPricingSettingsChange]
  );

  return (
    <ListingTableActionContext.Provider value={contextValue}>
      {children}
      {dialogRequest && (
        <AlertWithSuppressionDialog
          headerText={<Content id={ContentId.ManualPriceWarning} />}
          size="md"
          {...warningDialog.dialogProps}
          alerts={dialogRequest.alerts}
          onOkay={() => {
            dialogRequest.onOkay();
            warningDialog.closeDialog();
            setDialogRequest(null);
          }}
          onCancel={() => {
            dialogRequest.onCancel();
            warningDialog.closeDialog();
            setDialogRequest(null);
          }}
          viagogoVirtualId={dialogRequest.viagogoVirtualId}
        />
      )}
    </ListingTableActionContext.Provider>
  );
};

export const ListingTableActionContextWrapper = ({
  children,
  viagogoVirtualIds,
}: PropsWithChildren & {
  viagogoVirtualIds?: string | string[];
}) => {
  const hasAvoidReRenderListingTableRowFeature = useUserHasFeature(
    Feature.FEPerf_AvoidReRenderListingTableRow
  );
  const hasReworkListingPriceFormFeature = useUserHasFeature(
    Feature.ReworkListingPriceForm
  );

  const santitizedIds = useMemo(() => {
    if (!viagogoVirtualIds) {
      return [];
    }
    if (!Array.isArray(viagogoVirtualIds)) {
      return [viagogoVirtualIds];
    }
    return Array.from(new Set(viagogoVirtualIds)).toSorted();
  }, [viagogoVirtualIds]);

  if (
    hasAvoidReRenderListingTableRowFeature &&
    hasReworkListingPriceFormFeature
  ) {
    return (
      <ListingTableActionContextProvider viagogoVirtualIds={santitizedIds}>
        {children}
      </ListingTableActionContextProvider>
    );
  }
  return <>{children}</>;
};
